import {
  formatCalendarDay,
  lastDayOfMonth,
  toCalendarDay,
} from '../../../utils/dateTimeUtils';

export type LiksDateDayRange = {
  fromDay: string;
  toDay: string;
};

export type LiksDateRangePreset =
  | 'today'
  | 'thisWeek'
  | 'thisMonth'
  | 'last7Days'
  | 'last30Days'
  | 'entireTime';

export type LiksDateCalendarCell = {
  day: string;
  inMonth: boolean;
};

/** Monday-based week start for local calendar. */
export function resolveLiksDateRangePreset(
  preset: LiksDateRangePreset,
  now: Date = new Date(),
): LiksDateDayRange | null {
  const today = toCalendarDay(now);

  if (preset === 'entireTime') {
    return null;
  }

  if (preset === 'today') {
    return { fromDay: today, toDay: today };
  }

  if (preset === 'thisMonth') {
    return {
      fromDay: formatCalendarDay(now.getFullYear(), now.getMonth() + 1, 1),
      toDay: today,
    };
  }

  if (preset === 'last7Days') {
    const start = new Date(now);
    start.setDate(start.getDate() - 6);
    return { fromDay: toCalendarDay(start), toDay: today };
  }

  if (preset === 'last30Days') {
    const start = new Date(now);
    start.setDate(start.getDate() - 29);
    return { fromDay: toCalendarDay(start), toDay: today };
  }

  // thisWeek — Monday through today
  const start = new Date(now);
  const weekday = start.getDay();
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  start.setDate(start.getDate() + mondayOffset);
  return { fromDay: toCalendarDay(start), toDay: today };
}

export function formatLiksDateDayLabel(day: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${day}T00:00:00`));
}

export function formatLiksDateRangeLabel(
  range: LiksDateDayRange | null,
  locale: string,
  entireTimeLabel: string,
): string {
  if (range == null) {
    return entireTimeLabel;
  }
  const fromLabel = formatLiksDateDayLabel(range.fromDay, locale);
  const toLabel = formatLiksDateDayLabel(range.toDay, locale);
  if (range.fromDay === range.toDay) {
    return fromLabel;
  }
  return `${fromLabel} – ${toLabel}`;
}

/** Build a Monday-start calendar grid for a month (1–12). */
export function buildLiksDateMonthGrid(
  year: number,
  month: number,
): LiksDateCalendarCell[] {
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const mondayIndex = firstWeekday === 0 ? 6 : firstWeekday - 1;
  const daysInMonth = lastDayOfMonth(year, month);
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;
  const daysInPrevMonth = lastDayOfMonth(prevYear, prevMonth);

  const cells: LiksDateCalendarCell[] = [];

  for (let i = mondayIndex - 1; i >= 0; i -= 1) {
    const day = daysInPrevMonth - i;
    cells.push({
      day: formatCalendarDay(prevYear, prevMonth, day),
      inMonth: false,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      day: formatCalendarDay(year, month, day),
      inMonth: true,
    });
  }

  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  let nextDay = 1;
  while (cells.length % 7 !== 0) {
    cells.push({
      day: formatCalendarDay(nextYear, nextMonth, nextDay),
      inMonth: false,
    });
    nextDay += 1;
  }

  return cells;
}

export function shiftCalendarMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const date = new Date(year, month - 1 + delta, 1);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
  };
}
